import { styled, useTheme } from '@mui/material/styles';
import { useCallback, useState } from 'react';
import { ContinueStepButton } from '../../atoms/BasicButton/BasicButton';
import { Pagination } from '../../atoms/Pagination';
import { AdminFilters } from '../../molecules/AdminFilters';
import { AdminTableItem } from '../../molecules/AdminTable';
import { ConsoleTable } from '../../molecules/AdminTable/AdminTableRevamp';
import { useTranslation } from 'react-i18next';
import { AdvisorConsoleStore, AdvisorConsoleStoreFilters } from 'features/advisorConsole/types';
import { OptionType } from '../../molecules/AdminFilters/Dropdown';
import { Typography } from '../../atoms';
import { StickyRegion } from '../../../containers/sticky-region';
import { PdfDownloadToastState } from '../../../utils';
import { FailedToLoadContent } from '../FailedToLoadContent';
import { PdfDownloadErrorBanner } from '../../molecules/PdfDownloadErrorBanner/PdfDownloadErrorBanner';
import { PdfDownloadToast } from '../../molecules/Toast/PdfDownloadToast';

type Props = {
    items: AdminTableItem[];
    pagination: AdvisorConsoleStore['pagination'];
    // eslint-disable-next-line
    onChangePage(page: number): void;
    navigateToCreateClient(): void;
    // eslint-disable-next-line
    onChangeItemsPerPage(itemsPerPage: number): void;
    // eslint-disable-next-line
    onChangeFilters(filters: AdvisorConsoleStoreFilters): void;
    filters: AdvisorConsoleStoreFilters;
    clients: OptionType<string>[];
    loading?: boolean;
    hasApiError?: boolean;
    onRetry?: () => void;
};

export const AdvisorConsole = ({
    items,
    pagination,
    onChangePage,
    onChangeItemsPerPage,
    filters,
    onChangeFilters,
    navigateToCreateClient,
    clients = [],
    loading = false,
    hasApiError = false,
    onRetry
}: Props) => {
    const { t } = useTranslation();
    const [pdfToast, setPdfToast] = useState<PdfDownloadToastState>({ open: false, generating: false });
    const [pdfErrorPlanIds, setPdfErrorPlanIds] = useState<string[]>([]);
    const theme = useTheme();

    // Create an array of plan IDs whose PDF download failed.
    const updatePdfErrorPlan = useCallback((planId: string, hasError: boolean) => {
        setPdfErrorPlanIds((prev) => {
            if (hasError) return prev.includes(planId) ? prev : [...prev, planId];
            return prev.filter((id) => id !== planId);
        });
    }, []);

    // Show the banner only while at least one failed plan is in view and screen not loading.
    const showPdfErrorBanner = !loading && pdfErrorPlanIds.some((id) => items.some((item) => item.id === id));

    return (
        <Container>
            <StickyRegion>
                <StyledHeader>
                    <Typography as="h1" variant="headers.h2" flex={1} sx={{ color: theme.palette.primary[700] }}>
                        {t('ADVISOR_CONSOLE')}
                    </Typography>
                    {!hasApiError && (
                        <ContinueStepButton
                            variant="contained"
                            data-heap-id="create-new-plan"
                            onClick={navigateToCreateClient}
                        >
                            {t('TEXT_CREATE_A_NEW_PLAN')}
                        </ContinueStepButton>
                    )}
                </StyledHeader>
            </StickyRegion>
            {showPdfErrorBanner && (
                <div style={{ marginBottom: '16px' }}>
                    <PdfDownloadErrorBanner />
                </div>
            )}
            {hasApiError ? (
                <FailedToLoadContent onRetry={() => onRetry?.()} />
            ) : (
                <>
                    <div>
                        <AdminFilters filters={filters} onChangeFilters={onChangeFilters} clients={clients} />

                        <ConsoleTable
                            items={items}
                            loading={loading}
                            setPdfToast={setPdfToast}
                            setConsoleErrorBanner={updatePdfErrorPlan}
                            pdfErrorPlanIds={pdfErrorPlanIds}
                        />
                    </div>
                    <PaginationContainer>
                        <Pagination
                            onChangePage={onChangePage}
                            onChangeItemsPerPage={onChangeItemsPerPage}
                            totalPages={pagination.totalPages}
                            currentPage={pagination.page}
                            itemsPerPage={pagination.itemsPerPage}
                            totalItems={pagination.totalItems}
                        />
                    </PaginationContainer>
                    {pdfToast.open && pdfToast.type !== 'error' && (
                        <PdfDownloadToast
                            state={pdfToast}
                            onClose={() => setPdfToast({ open: false, generating: false })}
                        />
                    )}
                </>
            )}
        </Container>
    );
};

const Container = styled('main')(() => ({
    padding: '4px 40px 40px 40px',
    width: '100%',
    display: 'flex',
    flexDirection: 'column'
}));

const StyledHeader = styled('header')(() => ({
    display: 'flex',
    alignItems: 'center',
    padding: '24px 0px 32px 0px'
}));

const PaginationContainer = styled('div')(({ theme }) => ({
    display: 'flex',
    justifyContent: 'end',
    marginTop: 32,
    '& div span': {
        color: theme.palette.semantic.bg.brandDark,
        fontWeight: 500
    },
    '& div button': {
        color: theme.palette.semantic.bg.brandDark,
        fontWeight: 600
    },
    '& svg path': {
        fill: theme.palette.semantic.bg.brandDark
    }
}));
